<template>
   <div class="notification-bell">
      <Button
         ref="bellButtonRef"
         icon="pi pi-bell"
         text
         rounded
         aria-label="Notificaciones"
         @click="togglePanel"
      />
      <Badge
         v-if="total > 0"
         :value="total > 99 ? '99+' : String(total)"
         severity="danger"
         class="notification-badge"
      />
      <OverlayPanel
         ref="panelRef"
         class="notification-panel"
         :dismissable="false"
         @hide="onPanelHide"
      >
         <div
            class="notification-header flex align-items-center justify-content-between mb-2"
         >
            <div class="flex align-items-center gap-2">
               <span class="font-bold">Casos para cerrar</span>
               <Tag
                  v-if="total > 0"
                  :value="`${items.length} de ${total}`"
                  severity="danger"
               />
            </div>
            <Button icon="pi pi-times" text rounded @click="closePanel" />
         </div>
         <p class="text-sm text-color-secondary mt-0 mb-3">
            Presentado – Aguardando respuesta
         </p>

         <div v-if="loading && !items.length" class="text-center p-3">
            <ProgressSpinner style="width: 2rem; height: 2rem" />
         </div>
         <div
            v-else-if="!items.length"
            class="text-sm text-color-secondary p-2"
         >
            No hay casos pendientes.
         </div>
         <ul v-else class="notification-list list-none p-0 m-0">
            <li
               v-for="item in items"
               :key="item.hash_id"
               class="notification-item flex align-items-center justify-content-between gap-3 py-3 px-1 border-bottom-1 border-200"
               :class="{ 'notification-item--clickable': isNivel1 }"
               @click="isNivel1 ? goToSolicitud(item.hash_id) : undefined"
            >
               <div
                  class="notification-item__info flex flex-column gap-1 min-w-0"
               >
                  <span class="font-medium text-sm truncate">
                     #{{ item.id_poliza }} {{ item.cliente }}
                  </span>
                  <Tag
                     :value="`${Math.abs(item.dias_transcurridos)} días`"
                     :severity="
                        getSeverityByDays(Math.abs(item.dias_transcurridos))
                     "
                     class="notification-days-tag"
                     v-tooltip.bottom="
                        'Días en Presentado – Aguardando respuesta'
                     "
                  />
               </div>
               <div class="notification-item__action flex-shrink-0">
                  <Button
                     v-if="isNivel1"
                     label="Ver"
                     size="small"
                     @click.stop="goToSolicitud(item.hash_id)"
                  />
                  <Button
                     v-else-if="isBsaUser"
                     label="Resolver"
                     size="small"
                     @click="goToCase(item.hash_id)"
                  />
               </div>
            </li>
         </ul>

         <div v-if="isBsaUser && !isNivel1" class="mt-3 text-right">
            <Button label="Ver todos →" link class="p-0" @click="goToAll" />
         </div>
         <div v-else-if="isNivel1" class="mt-3 text-right">
            <Button
               label="Ver todos →"
               link
               class="p-0"
               @click="goToAllSolicitudes"
            />
         </div>
      </OverlayPanel>
   </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import OverlayPanel from 'primevue/overlaypanel';
import Badge from 'primevue/badge';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import { useParNotifications } from '@/composables/useParNotifications';
import { useProspectStatus } from '@/modules/bsa/composables/useStatus';

const STORAGE_KEY_PREFIX = 'par-notifications-intro-seen';

const router = useRouter();
const store = useStore();
const panelRef = ref(null);
const bellButtonRef = ref(null);
const { total, items, loading, fetchNotifications, startPolling, stopPolling } =
   useParNotifications();
const { getSeverityByDays } = useProspectStatus();

const isBsaUser = computed(() => store.state.auth.usuario?.id_empresa === 20);
const isNivel1 = computed(() => Number(store.state.auth.usuario?.nivel) === 1);
const isGpUser = computed(() => {
   const u = store.state.auth.usuario;
   const nivel = Number(u?.nivel);
   return Number(u?.id_grupo) === 1 && (nivel === 1 || nivel === 2);
});
const canSeeNotifications = computed(() => isBsaUser.value || isGpUser.value);
const shouldAutoOpenPanel = computed(() => {
   const nivel = Number(store.state.auth.usuario?.nivel);
   return Number.isFinite(nivel) && nivel < 2;
});
const usuarioId = computed(() => store.state.auth.usuario?.id_usuario);

const introStorageKey = computed(() =>
   usuarioId.value
      ? `${STORAGE_KEY_PREFIX}:${usuarioId.value}`
      : STORAGE_KEY_PREFIX,
);

const hasSeenIntro = () => {
   try {
      return localStorage.getItem(introStorageKey.value) === '1';
   } catch {
      return false;
   }
};

const markIntroSeen = () => {
   try {
      localStorage.setItem(introStorageKey.value, '1');
   } catch {
      // ignore quota / private mode
   }
};

const getBellTarget = () => {
   const btn = bellButtonRef.value;
   return btn?.$el ?? btn ?? null;
};

const openPanel = () => {
   const target = getBellTarget();
   if (!target || !panelRef.value) return;
   panelRef.value.show({ currentTarget: target, target });
};

const togglePanel = (event) => {
   panelRef.value?.toggle(event);
};

const closePanel = () => {
   markIntroSeen();
   panelRef.value?.hide();
};

const onPanelHide = () => {
   markIntroSeen();
};

const goToCase = (hashId) => {
   closePanel();
   router.push({
      path: '/multicotizador/bsa',
      query: { tab: 'casos', hash_id: hashId },
   });
};

const goToAll = () => {
   closePanel();
   router.push({
      path: '/multicotizador/bsa',
      query: { tab: 'casos' },
   });
};

const goToSolicitud = (hashId) => {
   closePanel();
   router.push({
      name: 'poliza/solicitudes',
      query: { hash_id: hashId },
   });
};

const goToAllSolicitudes = () => {
   closePanel();
   router.push({ name: 'poliza/solicitudes' });
};

onMounted(async () => {
   if (!canSeeNotifications.value) return;
   await fetchNotifications();
   startPolling();

   if (shouldAutoOpenPanel.value && !hasSeenIntro() && total.value > 0) {
      await nextTick();
      openPanel();
   }
});

onUnmounted(() => {
   stopPolling();
});

defineExpose({ canSeeNotifications, isBsaUser });
</script>

<style scoped>
.notification-bell {
   position: relative;
   display: inline-flex;
   align-items: center;
   margin-right: 1.25rem;
}
.notification-badge {
   position: absolute;
   top: 0;
   right: 0;
   transform: translate(25%, -25%);
   pointer-events: none;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   min-width: 1.25rem;
   height: 1.25rem;
   padding: 0 0.3rem;
   font-size: 0.65rem;
   line-height: 1;
   text-align: center;
}
.notification-panel {
   width: min(26rem, 92vw);
}
.notification-list {
   max-height: 18rem;
   overflow-y: auto;
}
.notification-item__info {
   flex: 1 1 auto;
   padding-right: 0.5rem;
}
.notification-days-tag,
:deep(.notification-days-tag.p-tag) {
   width: fit-content !important;
   white-space: nowrap;
   font-size: 0.65rem;
   padding: 0 0.35rem;
   line-height: 15px;
   min-height: 15px;
   display: inline-flex;
   align-items: center;
}
.notification-item__action {
   margin-left: 0.75rem;
}
.notification-item--clickable {
   cursor: pointer;
}
.notification-item--clickable:hover {
   background: var(--surface-hover, #f5f5f5);
}
</style>
